import React from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import { ArrowRight } from "lucide-react";

const BRAND = {
  burgundy: "#800020",
  gold: "#b08d57",
  deepGreen: "#006400",
  charcoal: "#0b0b0f",
};

const ECHOES_ARTICLES = [
  {
    tag: "Essay",
    title: "The Archive Is Alive",
    blurb:
      "How African memory continues to move through sound, style, language, family, and digital culture.",
    to: "/echoes/the-archive-is-alive",
  },
  {
    tag: "Reflection",
    title: "What We Carry Into the Future",
    blurb:
      "A meditation on inheritance, aspiration, and the emotional systems that shape African creative identity.",
    to: "/echoes/what-we-carry-into-the-future",
  },
  {
    tag: "Diaspora",
    title: "Home as a Moving Feeling",
    blurb:
      "On belonging, migration, cultural distance, and the ways Africa is remembered across borders.",
    to: "/echoes/home-as-a-moving-feeling",
  },
  {
    tag: "Culture",
    title: "The Small Signs That Stay",
    blurb:
      "From hairstyles to slang, family rituals to street sounds, the everyday details that become cultural memory.",
    to: "/echoes/the-small-signs-that-stay",
  },
  {
    tag: "Opinion",
    title: "Why Nostalgia Is Not Enough",
    blurb:
      "Looking beyond aesthetic revival towards cultural systems that can build real creative futures.",
    to: "/echoes/why-nostalgia-is-not-enough",
  },
  {
    tag: "Identity",
    title: "Soft Power Begins at Home",
    blurb:
      "How African cultural confidence is formed before it is exported, branded, or made global.",
    to: "/echoes/soft-power-begins-at-home",
  },
];

function Rule({ className = "" }) {
  return <div className={"h-px w-full bg-black/10 " + className} />;
}

function Box({ children, className = "" }) {
  return (
    <div className={"border border-black/15 bg-white " + className}>
      {children}
    </div>
  );
}

function TagPill({ children }) {
  return (
    <span
      className="inline-flex items-center border px-2 py-1 text-[11px] uppercase tracking-[0.18em] font-semibold"
      style={{
        borderColor: BRAND.gold + "66",
        backgroundColor: BRAND.gold + "12",
      }}
    >
      {children}
    </span>
  );
}

function EchoesHeader() {
  return (
    <header className="bg-white">
      <div className="mx-auto max-w-6xl px-4 sm:px-6 pt-6">
        <div className="flex items-center justify-between gap-4">
          <Link
            to="/"
            className="text-[11px] font-semibold tracking-[0.18em] uppercase text-neutral-600 hover:text-black"
          >
            Africa Loud
          </Link>

          <div className="flex items-center gap-4">
            <Link
              to="/film"
              className="text-[11px] font-semibold tracking-[0.18em] uppercase text-neutral-600 hover:text-black"
            >
              African Film
            </Link>

            <Link
              to="/ams"
              className="text-[11px] font-semibold tracking-[0.18em] uppercase text-neutral-600 hover:text-black"
            >
              AMS
            </Link>
          </div>
        </div>

        <Rule className="mt-5" />
      </div>
    </header>
  );
}

function HeroSection() {
  return (
    <section className="mx-auto max-w-6xl px-4 sm:px-6">
      <div className="grid gap-6 pt-8 lg:grid-cols-[1.2fr_0.8fr]">
        <Box className="p-7 sm:p-12 bg-[#fafafa]">
          <div className="flex items-center gap-3">
            <div
              className="h-2 w-2"
              style={{ backgroundColor: BRAND.gold }}
            />
            <div className="text-[11px] font-bold tracking-[0.25em] uppercase text-neutral-600">
              Echoes
            </div>
          </div>

          <h1 className="mt-8 font-serif text-5xl leading-[1.02] tracking-tight text-neutral-950 sm:text-7xl">
            Memory, identity, and the afterlife of culture.
          </h1>

          <p className="mt-7 max-w-2xl text-lg leading-relaxed text-neutral-650">
            Echoes is Africa Loud's reflective editorial space for cultural
            memory, personal essays, diaspora feeling, spiritual texture, and
            the quiet signals that continue to shape African creative life.
          </p>

          <div className="mt-10 flex flex-wrap gap-3">
            <span className="border border-black/15 px-3 py-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-neutral-700">
              Memory
            </span>
            <span className="border border-black/15 px-3 py-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-neutral-700">
              Diaspora
            </span>
            <span className="border border-black/15 px-3 py-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-neutral-700">
              Identity
            </span>
            <span className="border border-black/15 px-3 py-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-neutral-700">
              Reflection
            </span>
          </div>
        </Box>

        <Box className="p-7 sm:p-9 flex flex-col justify-between min-h-[420px]">
          <div>
            <div className="text-[11px] font-bold tracking-[0.22em] uppercase text-neutral-500">
              Editorial Note
            </div>

            <h2 className="mt-5 font-serif text-3xl leading-tight text-neutral-950">
              Some cultural movements are loud. Others return as echoes.
            </h2>

            <p className="mt-5 text-sm leading-relaxed text-neutral-600">
              This vertical gives space to the emotional residue of culture:
              the things remembered, inherited, repeated, remixed, protected,
              and carried forward.
            </p>
          </div>

          <div className="mt-10 border-t border-black/10 pt-6">
            <div className="text-[10px] font-bold tracking-[0.2em] uppercase text-neutral-400">
              Africa Loud Editorial
            </div>
            <div className="mt-2 font-serif text-xl text-neutral-950">
              Cultural memory as creative infrastructure.
            </div>
          </div>
        </Box>
      </div>
    </section>
  );
}

function FeaturedEssay() {
  return (
    <section className="mx-auto max-w-6xl px-4 sm:px-6 mt-8">
      <div className="mb-4 flex items-center gap-3">
        <div
          className="h-2 w-2"
          style={{ backgroundColor: BRAND.burgundy }}
        />
        <span className="text-[11px] uppercase tracking-[0.22em] font-bold text-neutral-600">
          Featured Echo
        </span>
      </div>

      <Link to="/echoes/the-archive-is-alive">
        <Box className="p-8 sm:p-10 hover:shadow-xl transition-shadow duration-500">
          <TagPill>Opening Essay</TagPill>

          <h2 className="mt-6 max-w-4xl font-serif text-4xl leading-tight tracking-tight sm:text-6xl">
            The Archive Is Alive
          </h2>

          <p className="mt-6 max-w-2xl text-lg leading-relaxed text-neutral-600">
            African culture does not only live in museums, books, or formal
            archives. It lives in gestures, songs, family memory, street
            language, beauty rituals, internet jokes, and the way people keep
            remaking themselves without asking for permission.
          </p>

          <div className="mt-8 inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-widest">
            Read the essay
            <ArrowRight className="h-4 w-4" />
          </div>
        </Box>
      </Link>
    </section>
  );
}

function ArticleGrid() {
  return (
    <section className="mx-auto max-w-6xl px-4 sm:px-6 mt-10">
      <div className="flex items-end justify-between gap-5">
        <div>
          <div className="text-[11px] font-bold tracking-[0.25em] uppercase text-neutral-500">
            Latest in Echoes
          </div>
          <h2 className="mt-3 font-serif text-3xl tracking-tight text-neutral-950">
            Essays, reflections, and cultural memory.
          </h2>
        </div>
      </div>

      <div className="mt-7 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {ECHOES_ARTICLES.map((article) => (
          <Link key={article.title} to={article.to} className="group">
            <Box className="p-6 h-full transition-all duration-300 group-hover:border-black/30 group-hover:shadow-lg">
              <TagPill>{article.tag}</TagPill>

              <div className="mt-4 font-serif text-2xl leading-snug tracking-tight text-neutral-950 group-hover:text-neutral-700 transition-colors">
                {article.title}
              </div>

              <p className="mt-3 text-sm text-neutral-600 leading-relaxed">
                {article.blurb}
              </p>

              <div className="mt-6 inline-flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.18em] text-neutral-500 group-hover:text-black">
                Read echo
                <ArrowRight className="h-3 w-3" />
              </div>
            </Box>
          </Link>
        ))}
      </div>
    </section>
  );
}

function ClosingStrip() {
  return (
    <section className="mx-auto max-w-6xl px-4 sm:px-6 mt-10">
      <div
        className="p-7 sm:p-10 text-white border"
        style={{
          backgroundColor: BRAND.charcoal,
          borderColor: BRAND.charcoal,
        }}
      >
        <div className="grid gap-6 lg:grid-cols-[0.8fr_1.2fr] lg:items-center">
          <div>
            <div className="text-[11px] font-bold tracking-[0.25em] uppercase text-white/50">
              Submit to Echoes
            </div>

            <h2 className="mt-3 font-serif text-3xl leading-tight text-white">
              Have a reflection, memory, or cultural essay?
            </h2>
          </div>

          <div>
            <p className="text-sm leading-relaxed text-white/70">
              Echoes is open to thoughtful writing on African identity,
              cultural inheritance, diaspora memory, spirituality, beauty,
              language, music, fashion, and the quiet emotional systems behind
              culture.
            </p>

            <a
              href="mailto:contact@africaloudmag.com?subject=Echoes%20Submission"
              className="mt-6 inline-flex items-center justify-center border border-white/30 px-5 py-3 text-[11px] font-semibold tracking-[0.18em] uppercase text-white hover:bg-white hover:text-black transition"
            >
              Submit a piece
              <ArrowRight className="ml-2 h-3 w-3" />
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}

function EchoesFooter() {
  return (
    <footer className="mt-12 bg-neutral-900 px-5 py-10">
      <div className="mx-auto max-w-6xl">
        <div className="flex flex-col gap-6 sm:flex-row sm:items-center sm:justify-between">
          <div className="text-lg font-bold text-white font-serif">
            Africa Loud
          </div>

          <div className="flex flex-wrap gap-x-5 gap-y-2">
            <Link
              to="/"
              className="text-[11px] font-semibold tracking-[0.18em] uppercase text-white/70 hover:text-white transition"
            >
              Home
            </Link>
            <Link
              to="/film"
              className="text-[11px] font-semibold tracking-[0.18em] uppercase text-white/70 hover:text-white transition"
            >
              African Film
            </Link>
            <Link
              to="/ams"
              className="text-[11px] font-semibold tracking-[0.18em] uppercase text-white/70 hover:text-white transition"
            >
              AMS
            </Link>
            <Link
              to="/collab"
              className="text-[11px] font-semibold tracking-[0.18em] uppercase text-white/70 hover:text-white transition"
            >
              Collab
            </Link>
          </div>
        </div>

        <div className="mt-8 pt-6 border-t border-white/10 text-[10px] tracking-widest uppercase text-white/40">
          &copy; 2026 Africa Loud. All rights reserved.
        </div>
      </div>
    </footer>
  );
}

export default function EchoesPage() {
  return (
    <div className="min-h-screen bg-white text-neutral-950">
      <Helmet>
        <title>Echoes | Africa Loud</title>
        <meta
          name="description"
          content="Echoes is Africa Loud's reflective editorial space for cultural memory, identity, diaspora, and African creative thought."
        />
      </Helmet>

      <style>
        {`
          @import url('https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,400;700;800&family=Outfit:wght@300;400;600;700&display=swap');

          body, div, p, span, button, a {
            font-family: 'Outfit', sans-serif;
          }

          .font-serif, h1, h2, h3, h4 {
            font-family: 'Fraunces', serif !important;
            font-weight: 700;
          }
        `}
      </style>

      <EchoesHeader />
      <HeroSection />
      <FeaturedEssay />
      <ArticleGrid />
      <ClosingStrip />
      <EchoesFooter />
    </div>
  );
}